import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { ChangeEvent, MouseEvent } from 'react';
import React, { useMemo, useState } from 'react';

import DotsLoader from 'components/DotsLoader/DotsLoader';

import type { DashboardI } from '../../declarations/reporting';

import { SelectsWrapperStyled } from './SelectCategories.styled';

export interface DashboardCategoryReportSelectProps {
  dashboards: DashboardI[] | undefined;
  value: DashboardI;
  onChange: (dashboard: DashboardI) => void;
  isLoading: boolean;
  categoryValue?: string | null;
  onCategoryChange?: (category: string | null) => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

function splitName(name: string): { category: string; report: string } {
  const idx = name.indexOf(' - ');
  if (idx === -1) return { category: 'Other', report: name };
  return { category: name.slice(0, idx), report: name.slice(idx + 3) };
}

function findById(
  list: DashboardI[] | undefined,
  id: string | null | undefined
): DashboardI | undefined {
  if (!id) return undefined;
  return list?.find((d): boolean => d?.dashboardId === id) ?? undefined;
}

const SelectCategoryReport: React.FC<DashboardCategoryReportSelectProps> = ({
  dashboards,
  value,
  onChange,
  categoryValue,
  onCategoryChange,
  fullWidth: _fullWidth = true,
  size: _size = 'small',
  disabled = false,
  isLoading,
}): JSX.Element => {
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<HTMLElement | null>(null);
  const [reportMenuAnchor, setReportMenuAnchor] = useState<HTMLElement | null>(null);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [reportSearchQuery, setReportSearchQuery] = useState('');

  // Build category -> dashboards map
  const { categories, byCategory } = React.useMemo((): {
    categories: string[];
    byCategory: Map<string, DashboardI[]>;
  } => {
    const map = new Map<string, DashboardI[]>();
    const order: string[] = [];
    dashboards?.forEach((d): void => {
      const { category } = splitName(d.dashboardName);
      if (!map.has(category)) {
        map.set(category, []);
        order.push(category);
      }
      const categoryDashboards = map.get(category);
      if (categoryDashboards) {
        categoryDashboards.push(d);
      }
    });
    return { categories: order, byCategory: map };
  }, [dashboards]);

  // Derive category from current selected dashboard
  const selectedItem = useMemo(
    () => findById(dashboards, value?.dashboardId ?? null),
    [dashboards, value]
  );
  const derivedCategoryFromValue = selectedItem
    ? splitName(selectedItem.dashboardName).category
    : null;

  // Category controlled/uncontrolled
  const isCategoryControlled = categoryValue !== undefined;
  const [internalCategory, setInternalCategory] = React.useState<string>('');
  const selectedCategory =
    (isCategoryControlled ? categoryValue : internalCategory) || derivedCategoryFromValue || '';

  React.useEffect((): void => {
    if (isCategoryControlled) return;
    setInternalCategory(derivedCategoryFromValue || '');
  }, [isCategoryControlled, derivedCategoryFromValue]);

  const selectedBelongsToCategory = React.useMemo(() => {
    if (!selectedItem || !selectedCategory) return false;
    return splitName(selectedItem.dashboardName).category === selectedCategory;
  }, [selectedItem, selectedCategory]);

  const selectedReportId = selectedBelongsToCategory ? selectedItem?.dashboardId || '' : '';

  const reports = useMemo(
    (): DashboardI[] => (selectedCategory ? byCategory.get(selectedCategory) || [] : []),
    [selectedCategory, byCategory]
  );

  const filteredCategories = useMemo(
    (): string[] =>
      categories.filter(
        (cat): boolean =>
          !categorySearchQuery.trim() ||
          cat.toLowerCase().includes(categorySearchQuery.trim().toLowerCase())
      ),
    [categories, categorySearchQuery]
  );

  const filteredReports = useMemo(
    (): DashboardI[] =>
      reports.filter(
        (item): boolean =>
          !reportSearchQuery.trim() ||
          splitName(item.dashboardName)
            .report.toLowerCase()
            .includes(reportSearchQuery.trim().toLowerCase())
      ),
    [reports, reportSearchQuery]
  );

  const handleCategorySelect = (category: string): void => {
    setCategoryMenuAnchor(null);
    setCategorySearchQuery('');
    if (!isCategoryControlled) setInternalCategory(category);
    onCategoryChange?.(category || null);
  };

  const handleReportSelect = (item: DashboardI): void => {
    setReportMenuAnchor(null);
    setReportSearchQuery('');
    onChange(item);
  };

  const buttonSx = {
    minWidth: 100,
    justifyContent: 'space-between' as const,
    fontSize: 12,
    height: 28,
    textTransform: 'none' as const,
  };

  const menuPaperSx = {
    maxHeight: 380,
    minWidth: 200,
    width: 'max-content' as const,
    maxWidth: 500,
    display: 'flex',
    flexDirection: 'column',
  };

  const menuListSx = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    p: 0,
    overflow: 'hidden',
  };

  return (
    <SelectsWrapperStyled>
      <Button
        variant="outlined"
        size="small"
        disabled={disabled}
        sx={buttonSx}
        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
        onClick={(e: MouseEvent<HTMLButtonElement>): void => {
          setCategorySearchQuery('');
          setCategoryMenuAnchor(e.currentTarget);
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <DotsLoader />
          </Box>
        ) : (
          <>Category{selectedCategory ? `: ${selectedCategory}` : ''}</>
        )}
      </Button>
      <Menu
        anchorEl={categoryMenuAnchor}
        open={Boolean(categoryMenuAnchor)}
        onClose={(): void => {
          setCategoryMenuAnchor(null);
          setCategorySearchQuery('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: menuPaperSx }}
        MenuListProps={{ sx: menuListSx }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search categories"
            value={categorySearchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>): void =>
              setCategorySearchQuery(e.target.value)
            }
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: categorySearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear category search"
                    size="small"
                    onClick={(): void => setCategorySearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {filteredCategories.map(
            (cat): JSX.Element => (
              <MenuItem
                key={cat}
                onClick={(): void => handleCategorySelect(cat)}
                selected={selectedCategory === cat}
              >
                <ListItemText primary={cat} sx={{ '& span': { whiteSpace: 'nowrap' } }} />
              </MenuItem>
            )
          )}
          {categorySearchQuery.trim() && filteredCategories.length === 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                px: 2,
                py: 1,
              }}
            >
              No categories match your search.
            </Typography>
          )}
        </List>
      </Menu>

      <Button
        variant="outlined"
        size="small"
        disabled={disabled || isLoading || !selectedCategory}
        sx={buttonSx}
        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
        onClick={(e: MouseEvent<HTMLButtonElement>): void => {
          setReportSearchQuery('');
          setReportMenuAnchor(e.currentTarget);
        }}
      >
        Report
        {selectedReportId && selectedItem
          ? `: ${splitName(selectedItem.dashboardName).report}`
          : ''}
      </Button>
      <Menu
        anchorEl={reportMenuAnchor}
        open={Boolean(reportMenuAnchor)}
        onClose={(): void => {
          setReportMenuAnchor(null);
          setReportSearchQuery('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: menuPaperSx }}
        MenuListProps={{ sx: menuListSx }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search reports"
            value={reportSearchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>): void =>
              setReportSearchQuery(e.target.value)
            }
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: reportSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear report search"
                    size="small"
                    onClick={(): void => setReportSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {filteredReports.map(
            (item): JSX.Element => (
              <MenuItem
                key={item.dashboardId}
                onClick={(): void => handleReportSelect(item)}
                selected={selectedReportId === item.dashboardId}
              >
                <ListItemText
                  primary={splitName(item.dashboardName).report}
                  sx={{ '& span': { whiteSpace: 'nowrap' } }}
                />
              </MenuItem>
            )
          )}
          {reportSearchQuery.trim() && filteredReports.length === 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                px: 2,
                py: 1,
              }}
            >
              No reports match your search.
            </Typography>
          )}
        </List>
      </Menu>
    </SelectsWrapperStyled>
  );
};

export default SelectCategoryReport;
